(function () {
  'use strict';

  const DEFAULT_RATES = Object.freeze({ base: 595, sizeStep: 50, extraStep: 50, streamers: 50 });
  const SIZES = Object.freeze({
    '16': { step: 0, bottle: 4, confetti: 60 },
    '24': { step: 1, bottle: 7, confetti: 80 },
    '31': { step: 2, bottle: 10, confetti: 100 }
  });
  const EXTRA_AMOUNTS = [0, 20, 40, 60, 80, 100];
  const QUANTITY_PRESETS = [500, 750, 1000];
  const MINIMUM_QUANTITY = 500;
  const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
  const number = new Intl.NumberFormat('en-US');

  function integer(value) {
    if (value === null || value === undefined || String(value).trim() === '') return null;
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) ? parsed : null;
  }

  function formatWeight(grams) {
    if (!Number.isFinite(grams) || grams < 0) return '—';
    // Display ounces rounded down; calculations and specifications retain exact grams.
    const ounces = Math.floor((grams / 28.349523125) * 10) / 10;
    return ounces.toFixed(1) + ' oz (' + grams + ' g)';
  }

  function formatBottleSize(centimeters) {
    if (!Number.isFinite(centimeters) || centimeters <= 0) return '—';
    return (centimeters / 2.54).toFixed(1) + ' in (' + centimeters + ' cm)';
  }

  // All arithmetic uses integer cents. The result is an estimate, never a checkout price.
  function calculateEstimate(input, rates = DEFAULT_RATES) {
    const errors = {};
    const size = SIZES[String(input.size)];
    const effect = input.effect;
    const quantity = integer(input.quantity);
    const extra = integer(input.extraFilling);
    if (!size) errors.size = 'Please choose a cannon size.';
    if (effect !== 'confetti' && effect !== 'streamers') errors.effect = 'Please choose confetti or streamers.';
    if (quantity === null || quantity < MINIMUM_QUANTITY || quantity > 100000) {
      errors.quantity = 'Enter a whole number between ' + MINIMUM_QUANTITY + ' and 100,000 cannons.';
    }
    if (!EXTRA_AMOUNTS.includes(extra)) errors.extraFilling = 'Please choose an extra filling amount.';
    if (!Object.keys(DEFAULT_RATES).every((key) => Number.isSafeInteger(rates[key]) && rates[key] >= 0) || rates.base === 0) {
      errors.rates = 'An estimate is unavailable. Please request a quote.';
    }
    if (Object.keys(errors).length) return { valid: false, errors };

    const baseCents = rates.base;
    const sizeCents = size.step * rates.sizeStep;
    const cannonCents = baseCents + sizeCents;
    const effectCents = effect === 'streamers' ? rates.streamers : 0;
    const extraCents = (extra / 20) * rates.extraStep;
    const unitCents = cannonCents + effectCents + extraCents;
    const totalCents = unitCents * quantity;
    if (!Number.isSafeInteger(totalCents)) {
      return { valid: false, errors: { rates: 'An estimate is unavailable. Please request a quote.' } };
    }
    return {
      valid: true,
      size: String(input.size), effect, quantity,
      baseCents, sizeCents, cannonCents, effectCents, extraCents, unitCents, totalCents,
      bottleCm: size.bottle,
      includedFillingGrams: size.confetti,
      totalFillingGrams: size.confetti + extra,
      includedConfettiGrams: effect === 'confetti' ? size.confetti : 0,
      extraFillingGrams: extra,
      extraConfettiGrams: effect === 'confetti' ? extra : 0,
      totalConfettiGrams: effect === 'confetti' ? size.confetti + extra : 0
    };
  }

  function quoteSummary(estimate) {
    if (!estimate.valid) return '';
    const filling = formatWeight(estimate.totalFillingGrams) + ' ' + estimate.effect
      + '; included: ' + formatWeight(estimate.includedFillingGrams) + '; extra: ' + formatWeight(estimate.extraFillingGrams)
      + (estimate.effect === 'streamers' ? '; streamers upgrade +' + money.format(estimate.effectCents / 100) + ' per cannon' : '');
    return [
      'CUSTOM CANNON ESTIMATE — USD',
      'Size: ' + estimate.size + ' in; iron bottle: ' + formatBottleSize(estimate.bottleCm),
      'Filling: ' + filling,
      'Colors and materials: Choose from paper, metallic, and biodegradable options. We’ll confirm colors and materials after your inquiry.',
      'Quantity: ' + number.format(estimate.quantity) + ' cannons; carton packing to be confirmed',
      'Per cannon: ' + estimate.size + ' in cannon ' + money.format(estimate.cannonCents / 100) + ' + filling ' + money.format(estimate.effectCents / 100) + ' + extra ' + estimate.effect + ' ' + money.format(estimate.extraCents / 100),
      'Estimated unit price: ' + money.format(estimate.unitCents / 100),
      'Estimated product total: ' + money.format(estimate.totalCents / 100),
      'Printing of the customer\'s supplied design is included. Custom volume pricing is already reflected; no additional standard-order discount applies.',
      'Minimum quantity: ' + MINIMUM_QUANTITY + ' cannons.',
      'Estimate only. We will follow up about design files and confirm specifications, packing and final pricing before production.'
    ].join('\n');
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { calculateEstimate, quoteSummary, formatWeight, formatBottleSize, DEFAULT_RATES };
  }

  if (typeof window === 'undefined' || !window.customElements || typeof HTMLElement === 'undefined') return;
  if (window.customElements.get('aaa-custom-estimator')) return;

  class AAACustomEstimator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.fields = {
        size: Array.from(this.querySelectorAll('[name="contact[size]"]')),
        effect: Array.from(this.querySelectorAll('[name="contact[effect]"]')),
        extraFilling: Array.from(this.querySelectorAll('[name="contact[extra_filling]"]')),
        quantity: Array.from(this.querySelectorAll('[name="contact[quantity]"]'))
      };
      this.quantityField = this.fields.quantity[0];
      this.quantityPresets = Array.from(this.querySelectorAll('[data-quantity-preset]'));
      this.summary = this.querySelector('[name="contact[estimate]"]');
      this.note = this.querySelector('[data-estimate-note]');
      this.form = this.querySelector('form');
      const rate = (attribute, fallback) => this.hasAttribute(attribute) ? integer(this.getAttribute(attribute)) : fallback;
      this.rates = {
        base: rate('data-base-cents', DEFAULT_RATES.base),
        sizeStep: rate('data-size-step-cents', DEFAULT_RATES.sizeStep),
        extraStep: rate('data-extra-step-cents', DEFAULT_RATES.extraStep),
        streamers: rate('data-streamers-cents', DEFAULT_RATES.streamers)
      };
      this.onChange = () => this.update();
      this.onPresetClick = (event) => {
        const button = event.target.closest('[data-quantity-preset]');
        if (!button || !this.quantityPresets.includes(button) || !this.quantityField) return;
        const quantity = integer(button.dataset.quantityPreset);
        if (!QUANTITY_PRESETS.includes(quantity)) return;
        this.quantityField.value = String(quantity);
        this.quantityField.dispatchEvent(new Event('input', { bubbles: true }));
      };
      this.onReset = () => {
        window.clearTimeout(this.resetTimer);
        this.resetTimer = window.setTimeout(() => this.update(), 0);
      };
      this.onSubmit = (event) => {
        if (!this.update()) {
          event.preventDefault();
          this.form.reportValidity();
        }
      };
      this.addEventListener('input', this.onChange);
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onPresetClick);
      if (this.form) {
        this.form.addEventListener('reset', this.onReset);
        this.form.addEventListener('submit', this.onSubmit);
      }
      this.update();
    }

    disconnectedCallback() {
      this.removeEventListener('input', this.onChange);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onPresetClick);
      if (this.form) {
        this.form.removeEventListener('reset', this.onReset);
        this.form.removeEventListener('submit', this.onSubmit);
      }
      window.clearTimeout(this.resetTimer);
      this.initialized = false;
    }

    update() {
      const input = {};
      Object.entries(this.fields).forEach(([key, fields]) => {
        const field = key === 'quantity' ? fields[0] : fields.find((option) => option.checked);
        input[key] = field ? field.value : null;
      });
      const streamers = input.effect === 'streamers';
      const fillingName = streamers ? 'streamers' : 'confetti';
      this.querySelectorAll('[data-extra-heading]').forEach((element) => { element.textContent = 'Add more ' + fillingName; });
      this.querySelectorAll('[data-extra-label]').forEach((element) => { element.textContent = 'Extra ' + fillingName; });
      this.quantityPresets.forEach((button) => {
        button.setAttribute('aria-pressed', String(integer(input.quantity) === integer(button.dataset.quantityPreset)));
      });
      const estimate = calculateEstimate(input, this.rates);
      Object.entries(this.fields).forEach(([key, fields]) => {
        fields.forEach((field) => { field.setCustomValidity(''); });
        const field = fields.find((option) => option.checked && !option.disabled) || fields.find((option) => !option.disabled);
        if (field && !estimate.valid) field.setCustomValidity(estimate.errors[key] || '');
      });
      if (this.quantityField) this.quantityField.setAttribute('aria-invalid', String(!estimate.valid && Boolean(estimate.errors.quantity)));
      if (this.summary) this.summary.value = quoteSummary(estimate);
      this.dataset.estimateValid = String(estimate.valid);
      const size = SIZES[String(input.size)];
      const hasFilling = input.effect === 'confetti' || streamers;
      const extra = integer(input.extraFilling);
      const fillingAmount = size && hasFilling && EXTRA_AMOUNTS.includes(extra) ? formatWeight(size.confetti + extra) : '—';
      const weightParts = fillingAmount.split(' (');
      const specifications = {
        cannonLabel: size ? input.size + ' in cannon' : '—',
        bottle: size ? formatBottleSize(size.bottle) : '—',
        fillingAmount,
        fillingOunces: weightParts[0],
        fillingGrams: weightParts[1] ? '(' + weightParts[1] : '—',
        fillingType: hasFilling ? streamers ? 'Streamers' : 'Confetti' : '—'
      };
      if (!estimate.valid) {
        this.querySelectorAll('[data-estimate]').forEach((element) => {
          element.textContent = specifications[element.dataset.estimate] || '—';
        });
        if (this.note) this.note.textContent = estimate.errors.quantity || estimate.errors.rates || 'Choose your options and enter at least ' + MINIMUM_QUANTITY + ' cannons to see your estimate.';
        return false;
      }
      const values = {
        unit: money.format(estimate.unitCents / 100),
        total: money.format(estimate.totalCents / 100),
        cannon: money.format(estimate.cannonCents / 100),
        effect: money.format(estimate.effectCents / 100),
        extra: money.format(estimate.extraCents / 100),
        ...specifications,
        quantity: number.format(estimate.quantity)
      };
      this.querySelectorAll('[data-estimate]').forEach((element) => {
        if (Object.hasOwn(values, element.dataset.estimate)) element.textContent = values[element.dataset.estimate];
      });
      if (this.note) {
        this.note.textContent = 'Minimum ' + MINIMUM_QUANTITY + ' cannons. Carton packing is flexible.';
      }
      return true;
    }
  }

  window.customElements.define('aaa-custom-estimator', AAACustomEstimator);
})();
